import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/apiConfig';
import { Container, Typography, Grid, CircularProgress, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getAffiliateById } from '../api/affiliateApi';

const Trainer = () => {
    const [trainerAffiliates, setTrainerAffiliates] = useState([]);
    const [selectedAffiliate, setSelectedAffiliate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    // API_URL imported from config;
    const token = localStorage.getItem("token");

    useEffect(() => {
        // Clear any existing localStorage affiliate data to ensure fresh selection
        localStorage.removeItem("affiliateId");
        localStorage.removeItem("affiliateName");
        localStorage.removeItem("selectedAffiliate");

        // Always fetch all trainer affiliates
        fetchTrainerAffiliates();
    }, []);

    // Function to fetch trainer affiliates
    const fetchTrainerAffiliates = async () => {
        try {
            const response = await fetch(`${API_URL}/trainer/affiliates`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch trainer affiliates');
            }

            const data = await response.json();
            setTrainerAffiliates(data);

            // If user is a trainer at only one affiliate, automatically select it
            if (data.length === 1) {
                try {
                    const affiliateData = await getAffiliateById(data[0].id);

                    // Ensure we have required fields
                    if (!affiliateData.id || !affiliateData.name) {
                        console.error("Affiliate data missing id or name:", affiliateData);

                        // Enhance data if needed
                        const enhancedData = {
                            ...affiliateData,
                            id: affiliateData.id || data[0].id,
                            name: affiliateData.name || data[0].name
                        };

                        // Save to localStorage for other components to use
                        localStorage.setItem("affiliateId", enhancedData.id);
                        localStorage.setItem("affiliateName", enhancedData.name);
                        localStorage.setItem("selectedAffiliate", JSON.stringify(enhancedData));

                        setSelectedAffiliate(enhancedData);
                    } else {
                        // Save to localStorage for other components to use
                        localStorage.setItem("affiliateId", affiliateData.id);
                        localStorage.setItem("affiliateName", affiliateData.name);
                        localStorage.setItem("selectedAffiliate", JSON.stringify(affiliateData));

                        setSelectedAffiliate(affiliateData);
                    }
                } catch (err) {
                    console.error("Error fetching affiliate:", err);
                    setError("Failed to load affiliate data");
                }
            }
            // If there are multiple affiliates, don't auto-select anything
            // User must choose manually
        } catch (err) {
            console.error("Error fetching trainer affiliates:", err);
            setError("Failed to load trainer data");
        } finally {
            setLoading(false);
        }
    };

    // Function to handle affiliate selection
    const setAffiliate = async (affiliate) => {
        try {
            // Load complete affiliate data
            const affiliateData = await getAffiliateById(affiliate.id);

            // Ensure we have required fields
            const finalData = {
                ...affiliateData,
                id: affiliateData.id || affiliate.id,
                name: affiliateData.name || affiliate.name
            };

            // Save to localStorage for other components to use
            localStorage.setItem("affiliateId", finalData.id);
            localStorage.setItem("affiliateName", finalData.name);
            localStorage.setItem("selectedAffiliate", JSON.stringify(finalData));

            setSelectedAffiliate(finalData);
        } catch (err) {
            console.error("Error fetching affiliate:", err);
            setError("Failed to load affiliate data");
        }
    };

    // Navigate to another page with affiliate state
    const navigateWithAffiliate = (path) => {
        navigate(path, { state: { affiliate: selectedAffiliate } });
    };

    if (loading) {
        return (
            <Container maxWidth={false} sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth={false} sx={{ mt: 4 }}>
                <Typography color="error" variant="h6">{error}</Typography>
            </Container>
        );
    }

    // If user has multiple affiliates and hasn't selected one, show selection menu
    if (trainerAffiliates.length > 1 && !selectedAffiliate) {
        return (
            <Container maxWidth={false} sx={{ mt: 4 }}>
                <Typography variant="h4" gutterBottom>
                    Select Affiliate
                </Typography>
                <Grid container spacing={2}>
                    {trainerAffiliates.map((affiliate) => (
                        <Grid item xs={12} sm={6} md={4} key={affiliate.id}>
                            <Paper
                                elevation={3}
                                sx={{
                                    p: 2,
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        backgroundColor: '#f5f5f5'
                                    }
                                }}
                                onClick={() => setAffiliate(affiliate)}
                            >
                                <Typography variant="h6">{affiliate.name}</Typography>
                                {affiliate.address && (
                                    <Typography variant="body2" color="text.secondary">
                                        {affiliate.address}
                                    </Typography>
                                )}
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        );
    }

    // Render trainer dashboard with selected affiliate
    return (
        <Container maxWidth={false} sx={{ mt: 4 }}>
            <Typography variant="h4" gutterBottom>
                Trainer Dashboard
            </Typography>
            {selectedAffiliate && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h5" gutterBottom>{selectedAffiliate.name}</Typography>
                    {selectedAffiliate.address && (
                        <Typography variant="body1">{selectedAffiliate.address}</Typography>
                    )}
                    {selectedAffiliate.trainingType && (
                        <Typography variant="body1">
                            Training type: {selectedAffiliate.trainingType}
                        </Typography>
                    )}
                </Paper>
            )}
        </Container>
    );
};

export default Trainer;